/**
 * UserPlatePicker — admin-only Autocomplete for selecting whose plate
 * to view on the homepage. "Company-wide" sentinel resets to default.
 */

import { useMemo } from 'react';
import { Autocomplete, TextField, Avatar, Box, Typography, Chip } from '@mui/material';
import { useProfiles } from '../../hooks/useProfiles';
import { orderProfilesForPicker } from '../../lib/platePeople';

const COMPANY_OPTION = { id: '__company__', fullName: 'Company-wide', role: '__sentinel__' };

const ROLE_LABEL = {
  super_admin: 'Super',
  admin: 'Admin',
  core_crew: 'Core',
  crew: 'Crew',
};

const ROLE_COLOR = {
  super_admin: '#D2691E',
  admin: '#7C3AED',
  core_crew: '#0EA5E9',
  crew: '#64748B',
};

export default function UserPlatePicker({ value, onChange }) {
  const { data: profiles = [] } = useProfiles();

  const options = useMemo(() => {
    return [COMPANY_OPTION, ...orderProfilesForPicker(profiles)];
  }, [profiles]);

  // Resolve current selection (value === null means Company-wide)
  const selected = useMemo(() => {
    if (!value) return COMPANY_OPTION;
    return options.find(o => o.id === value) || COMPANY_OPTION;
  }, [value, options]);

  return (
    <Autocomplete
      size="small"
      disableClearable
      options={options}
      value={selected}
      onChange={(_e, opt) => onChange(opt && opt.id !== COMPANY_OPTION.id ? opt.id : null)}
      getOptionLabel={(opt) => (opt?.fullName || opt?.full_name || opt?.email || '')}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      sx={{ minWidth: { xs: '100%', sm: 240 }, maxWidth: { xs: '100%', sm: 320 } }}
      renderOption={(props, opt) => {
        const isSentinel = opt.id === COMPANY_OPTION.id;
        const initials = (opt.fullName || opt.full_name || opt.email || '?')
          .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
        return (
          <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSentinel ? (
              <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏢</Box>
            ) : (
              <Avatar src={opt.avatarUrl || ''} sx={{ width: 24, height: 24, fontSize: 11, bgcolor: opt.accentColor || '#444' }}>
                {initials}
              </Avatar>
            )}
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
              {opt.fullName || opt.full_name || opt.email}
            </Typography>
            {!isSentinel && ROLE_LABEL[opt.role] && (
              <Chip
                size="small"
                label={ROLE_LABEL[opt.role]}
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  bgcolor: `${ROLE_COLOR[opt.role]}25`,
                  color: ROLE_COLOR[opt.role],
                  border: `1px solid ${ROLE_COLOR[opt.role]}40`,
                }}
              />
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Plate of"
          sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
        />
      )}
    />
  );
}
